import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { BaseButtonSize } from '../../../shared/components/button/base-button.component';
import { DocumentListItemResponse, UpdateCustomerLocationDocumentRequest } from '../../../../api';
import { baseFilesizeValidator } from '../../../shared/validators/base-filesize.validator';
import { baseFiletypeValidator } from '../../../shared/validators/base-filetype.validator';
import { BaseDateService } from '../../../shared/services/base-date.service';
import { BaseModalService } from '../../../shared/services/base-modal.service';
import { BaseCoreState, baseDocumentActions, baseDocumentSelectors } from '../../../_store';
import { BaseDocumentFormInterface } from '../../../modules/administration/_interfaces/base-master-data.interface';
import { BaseFileExtension } from '../../../shared/enums/base-available-file-type.enum';
import { DocumentListItemResponseType } from '../../../shared/interfaces/base-offer-item.interface';

@Component({
  selector: 'base-master-data-document',
  templateUrl: './base-master-data-document.component.html',
})
export class BaseMasterDataDocumentComponent implements OnInit, OnDestroy {
  @ViewChild('documentModalBody') documentModalBody: ElementRef;
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  @ViewChild('deleteDocumentModalTemplate') deleteDocumentModalTemplate: ElementRef;

  @Input() document: DocumentListItemResponseType;
  @Input() readonly = false;
  @Input() emitOnly = false;
  @Input() documentCategories: { value: number; label: string }[] = [];

  @Output() selectDocumentEvent = new EventEmitter<{ id: string; action: string; type: string }>();

  modalRef: NgbModalRef;
  deleteModalRef: NgbModalRef;
  documentForm: FormGroup<BaseDocumentFormInterface>;
  buttonSizes = BaseButtonSize;
  documentFormSubmitted = false;
  activeDocumentSrc: Blob;
  activeDocument: DocumentListItemResponse | undefined;
  documentViewerModalRef: NgbModalRef;
  baseFileExtension = BaseFileExtension;

  private destroy$ = new Subject<boolean>();

  constructor(
    private fb: NonNullableFormBuilder,
    private modalService: BaseModalService,
    private store: Store<BaseCoreState>
  ) {}

  ngOnInit(): void {
    this.store
      .select(baseDocumentSelectors.getDownloadDocument)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data),
        map((state) => state.data)
      )
      .subscribe((document) => {
        if (this.activeDocument && this.activeDocument.id === this.document.document.id) {
          this.openDocumentViewer(document as Blob);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  openEditDocumentModal(): void {
    this.selectDocumentEvent.emit({ id: this.document.document.id, action: 'edit', type: this.document.document.type });

    if (this.emitOnly) {
      return;
    }

    const doc = this.document.document;

    const selectedCategory = this.documentCategories.find((category) => doc.category === category.value) ?? null;

    this.documentForm = this.fb.group<BaseDocumentFormInterface>({
      name: this.fb.control<string>(doc.name, {
        validators: [Validators.required],
      }),
      category: this.fb.control<{ value: number; label: string } | null>(selectedCategory, {
        validators: [Validators.required],
      }),
      validUntil: this.fb.control<string | null>({ value: doc.validUntil ?? null, disabled: true }),
      private: this.fb.control<boolean>(false),
      file: this.fb.control<DocumentListItemResponse | File | null>(doc ?? null, {
        validators: [baseFilesizeValidator(), baseFiletypeValidator(), Validators.required],
      }),
    });

    this.modalRef = this.modalService.open(
      {
        headText: 'document.info',
        bodyTemplate: this.documentModalBody,
        okText: 'action.save',
        canBeClosed: this.canDocumentModalBeClosed.bind(this),
      },
      {
        size: 'lg',
      }
    );

    this.modalRef.result.then(
      () => {
        if (this.documentForm.valid) {
          const documentFormValue = this.documentForm.getRawValue();
          const body: UpdateCustomerLocationDocumentRequest = { name: documentFormValue.name };

          if (documentFormValue.category?.value) {
            body.category = documentFormValue.category.value;
          }

          if (documentFormValue.validUntil) {
            body.validUntil = BaseDateService.toServerFormatString(documentFormValue.validUntil);
          }

          this.store.dispatch(
            baseDocumentActions.updateDocument({
              payload: { document: this.document.document.id, body },
            })
          );

          if (documentFormValue.file) {
            this.store.dispatch(
              baseDocumentActions.changeDocumentFile({
                payload: {
                  document: this.document.document.id,
                  file: documentFormValue.file as Blob,
                },
              })
            );
          }
          this.resetDocumentForm();
        }
      },
      () => {
        this.resetDocumentForm();
      }
    );
  }

  downloadDocument(): void {
    this.selectDocumentEvent.emit({
      id: this.document.document.id,
      action: 'download',
      type: this.document.document.type,
    });

    if (this.emitOnly) {
      return;
    }

    this.store.dispatch(
      baseDocumentActions.downloadDocument({
        payload: { document: this.document.document.id, documentType: this.document.document.type },
      })
    );
  }

  openDocument(): void {
    this.activeDocument = this.document.document;
    this.selectDocumentEvent.emit({ id: this.document.document.id, action: 'open', type: this.document.document.type });

    if (this.emitOnly) {
      return;
    }

    this.store.dispatch(
      baseDocumentActions.downloadDocument({
        payload: { document: this.document.document.id, documentType: this.document.document.type, openInModal: true },
      })
    );
  }

  deleteDocument(): void {
    this.deleteModalRef = this.modalService.open(
      {
        headText: 'document.delete.title',
        bodyTemplate: this.deleteDocumentModalTemplate,
        okText: 'action.okDelete',
        cancelText: 'action.cancel',
      },
      {
        size: 'lg',
        backdrop: 'static',
      }
    );

    this.deleteModalRef.result.then(() => {
      this.selectDocumentEvent.emit({
        id: this.document.document.id,
        action: 'delete',
        type: this.document.document.type,
      });

      if (this.emitOnly) {
        return;
      }

      this.store.dispatch(baseDocumentActions.deleteDocument({ payload: { document: this.document.document.id } }));
    }, noop);
  }

  private resetDocumentForm(): void {
    this.documentForm.reset();
    this.documentFormSubmitted = false;
  }

  private canDocumentModalBeClosed(): boolean {
    this.documentFormSubmitted = true;
    return this.documentForm.valid;
  }

  private openDocumentViewer(document: Blob): void {
    this.activeDocumentSrc = document;
    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.documentViewerModalRef.result.then(
      () => this.cleanDocumentViewer(),
      () => this.cleanDocumentViewer()
    );
  }

  private cleanDocumentViewer() {
    this.store.dispatch(baseDocumentActions.clearDownloadDocument());
    this.activeDocument = undefined;
  }
}
